import { addCalendarDays, getAccountLocalDate } from '@repo/sdk'
import { Check, ChevronRight, CircleCheck, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'

import { formatCheckInDate } from '@/components/check-in/check-in-date'
import { Button } from '@/components/ui/button'
import { useCheckInHistory } from '@/hooks/use-check-in-history'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'

export function SevenDayCheckInStrip() {
  const { t, language } = useI18n()
  const history = useCheckInHistory()
  const { data } = history
  const isError = history.isError || history.calendar.isError
  const isLoading = history.isResolving || (history.hasIdentity && history.isPending && !isError)
  const today = data?.today ?? getAccountLocalDate(new Date(history.nowMs), history.calendar.timeZone)
  const checkedDates = new Set(data?.checkedDates ?? [])
  const days = Array.from({ length: 7 }, (_, index) => addCalendarDays(today, index - 6))
  const isTodayChecked = checkedDates.has(today)
  const retry = () => {
    if (history.calendar.isError)
      void history.calendar.initialize().then(() => history.refetch()).catch(() => {})
    else
      void history.refetch()
  }

  return (
    <section
      className="mt-4 border-t border-border pt-3"
      data-testid="check-in-seven-day-strip"
      aria-label={t('checkIn.recentSevenDays')}
      aria-busy={isLoading || undefined}
      dir={language === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="text-sm font-medium tabular-nums" data-testid="check-in-current-streak">
          {isLoading || isError ? t('checkIn.title') : t('checkIn.currentStreak', { count: data?.currentStreak ?? 0 })}
        </p>
        <Link
          to="/dashboard?tab=profile#check-in-history"
          className="inline-flex min-h-11 items-center gap-1 text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          data-testid="check-in-history-link"
        >
          {t('checkIn.viewHistory')}
          <ChevronRight className="size-3.5 rtl:rotate-180" aria-hidden="true" />
        </Link>
      </div>
      {isLoading ? (
        <p role="status" className="flex min-h-24 items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          {t('checkIn.loading')}
        </p>
      ) : isError ? (
        <div className="space-y-2 py-2">
          <p role="alert" className="text-xs text-foreground">{t('checkIn.loadError')}</p>
          <Button type="button" variant="outline" size="sm" onClick={retry}>{t('checkIn.retry')}</Button>
        </div>
      ) : (
        <>
          <ol className="grid grid-cols-7 gap-1" aria-label={t('checkIn.recentSevenDays')}>
            {days.map((day) => {
              const checked = checkedDates.has(day)
              const isToday = day === today
              const isUntracked = !data || day < data.trackingStartedOn
              const state = checked
                ? t('checkIn.completed')
                : isUntracked
                  ? t('checkIn.untracked')
                  : isToday ? t('checkIn.todayPending') : t('checkIn.missed')
              return (
                <li
                  key={day}
                  className="flex min-w-0 flex-col items-center gap-1.5 text-xs text-muted-foreground"
                  aria-label={`${formatCheckInDate(day, language, { dateStyle: 'full' })}, ${state}`}
                  aria-current={isToday ? 'date' : undefined}
                  data-date={day}
                  data-checked={checked}
                  data-state={checked ? 'completed' : isUntracked ? 'untracked' : isToday ? 'pending' : 'missed'}
                >
                  <span
                    className={cn(
                      'grid size-7 place-items-center rounded-full border border-transparent bg-muted tabular-nums',
                      checked && 'bg-primary text-primary-foreground',
                      isToday && 'border-primary ring-1 ring-primary ring-offset-2 ring-offset-card',
                      isUntracked && !isToday && 'opacity-50',
                    )}
                    aria-hidden="true"
                  >
                    {checked ? <Check className="size-3.5" /> : isToday ? Number(day.slice(-2)) : null}
                  </span>
                  <span aria-hidden="true">{isToday ? t('checkIn.today') : formatCheckInDate(day, language, { month: 'numeric', day: 'numeric' })}</span>
                </li>
              )
            })}
          </ol>
          <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-muted-foreground" data-testid="check-in-prompt" aria-live="polite">
            {isTodayChecked && <CircleCheck className="mt-0.5 size-3.5 shrink-0 text-primary" aria-hidden="true" />}
            {isTodayChecked ? t('checkIn.todayComplete') : (data?.totalDays ?? 0) === 0 ? t('checkIn.startPrompt') : t('checkIn.playPrompt')}
          </p>
        </>
      )}
    </section>
  )
}
